/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { mcpServersAddToolMetadataBatch } from "../funcs/mcpServersAddToolMetadataBatch.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { AddToolMetadataBatchResult } from "../models/components/addtoolmetadatabatchresult.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  AddMcpServerToolMetadataBatchRequest,
  AddMcpServerToolMetadataBatchSecurity,
} from "../models/operations/addmcpservertoolmetadatabatch.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type AddMcpServerToolMetadataBatchMutationVariables = {
  request: AddMcpServerToolMetadataBatchRequest;
  security?: AddMcpServerToolMetadataBatchSecurity | undefined;
  options?: RequestOptions;
};

export type AddMcpServerToolMetadataBatchMutationData =
  AddToolMetadataBatchResult;

export type AddMcpServerToolMetadataBatchMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * addToolMetadataBatch mcpServers
 *
 * @remarks
 * Strictly additive batch insert of tool metadata for an MCP server. Every tool in the payload is inserted; if any of them already has a live stored entry the whole batch fails with a conflict and nothing is inserted. Stored tools absent from the payload are left untouched and nothing is deleted. Callers are expected to send only tools they know are new.
 */
export function useAddMcpServerToolMetadataBatchMutation(
  options?: MutationHookOptions<
    AddMcpServerToolMetadataBatchMutationData,
    AddMcpServerToolMetadataBatchMutationError,
    AddMcpServerToolMetadataBatchMutationVariables
  >,
): UseMutationResult<
  AddMcpServerToolMetadataBatchMutationData,
  AddMcpServerToolMetadataBatchMutationError,
  AddMcpServerToolMetadataBatchMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildAddMcpServerToolMetadataBatchMutation(client, options),
    ...options,
  });
}

export function mutationKeyAddMcpServerToolMetadataBatch(): MutationKey {
  return ["@gram/client", "mcpServers", "addToolMetadataBatch"];
}

export function buildAddMcpServerToolMetadataBatchMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: AddMcpServerToolMetadataBatchMutationVariables,
  ) => Promise<AddMcpServerToolMetadataBatchMutationData>;
} {
  return {
    mutationKey: mutationKeyAddMcpServerToolMetadataBatch(),
    mutationFn: function addMcpServerToolMetadataBatchMutationFn({
      request,
      security,
      options,
    }): Promise<AddMcpServerToolMetadataBatchMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(mcpServersAddToolMetadataBatch(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
